// tslint:disable:max-file-line-count
import { mergeStyles, Tag } from '@cian/components';
import { Button } from '@cian/ui-kit';
import * as React from 'react';
import classNames from 'classnames';

import { getValueFromComputedStyle } from '../../../../browser/utils/dom';
import { Button as ButtonPrev } from '../../../common/components/button';
import { EMetroTravelType, IJsonQuery } from '../../../json_query';
import { TTag } from '../../state/tags';
import { getParamTitleHelper } from '../advanced/helpers';

import { FOfferType, offerTypeFromJsonQuery } from '../../../utils/category';
import FitInOneLineContainer from './fit_in_one_line/container';
import { AccessSystemTagItem } from './items/access_system';
import { AgentCallsForbiddenTagItem } from './items/agent_calls_forbidden';
import { ApartmentTagItem } from './items/apartment';
import { AreaTagItem } from './items/area';
import { BalconyTagItem } from './items/balcony';
import { BedroomsMinCountTagItem } from './items/bedrooms_min_count';
import { BSCenterTagItem } from './items/bs_center';
import { BuildingClassTypesTagItem } from './items/building_class';
import { WarehouseBuildingClassTypesTagItem } from './items/building_class_warehouse';
import { BuildingConditionTypesTagItem } from './items/building_condition';
import { BuildingCraneTypesTagItem } from './items/building_crane';
import { BuildingDoneTagItem } from './items/building_done';
import { BuildingElevatorTypesTagItem } from './items/building_elevator';
import { BuildingGatesTypesTagItem } from './items/building_gates';
import { BuildingHeatingTypesTagItem } from './items/building_heating';
import { BuildingTypeTagItem } from './items/building_type';
import { BuildingTypeBuildingTagItem } from './items/building_type.building';
import { CeilingHeightTagItem } from './items/ceiling_height';
import { ComissionTypeTagItem } from './items/comission_type';
import { ContractTypeTagItem } from './items/contract';
import { DecorationTagItem } from './items/decoration';
import { DeveloperTagItem } from './items/developer';
import { DomrfTagItem } from './items/domrf';
import { ElevatorsHasServiceTagItem } from './items/elevators_has_service';
import { ElevatorsMinCountTagItem } from './items/elevators_min_count';
import { EntranceTypesTagItem } from './items/entrance';
import { EstateTypeTagItem } from './items/estate_type';
import { FacilityTagItem } from './items/facility';
import { FloorTagItem } from './items/floor';
import { FurnitureTagItem } from './items/furniture';
import { GarageKindsTagItem } from './items/garage_kind';
import { GarageMaterialsTagItem } from './items/garage_material';
import { GarageStatusesTagItem } from './items/garage_status';
import { GarageTypesTagItem } from './items/garage_type';
import {
  GeoBuilderTagItem,
  GeoCircleTagItem,
  GeoDistrictTagItem,
  GeoHighwayTagItem,
  GeoHouseTagItem,
  GeoLocationTagItem,
  GeoNewobjectTagItem,
  GeoPolygonTagItem,
  GeoBboxTagItem,
  GeoStreetTagItem,
  GeoUndergroundTagItem,
} from './items/geo';
import { HeatingTypesTagItem } from './items/heating';
import { HiddenBaseTagItem } from './items/hidden_base';
import { HouseLineBuildingTag } from './items/house_line.building';
import { HouseLineShoppingAreaTag } from './items/house_line.shopping_area';
import { HouseMaterialTagItem } from './items/house_material';
import { HouseYearTagItem } from './items/house_year';
import { InfrastructureParamTagItem } from './items/infrastructure';
import { IsBasementFloorTagItem } from './items/is_basement_floor';
import { IsFirstFloorTagItem } from './items/is_first_floor';
import { IsSemibasementFloorTagItem } from './items/is_semibasement_floor';
import { KpIdTagItem } from './items/kp_id';
import { LandStatusTypesTagItem } from './items/land_status';
import { LayoutTypeTagItem } from './items/layout';
import { LoggiaTagItem } from './items/loggia';
import { ManufactureConditionTypesTagItem } from './items/manufacture_condition';
import { MaxPrepayTagItem } from './items/max_prepay';
import { MortgagePossibleTagItem } from './items/mortgage_possible';
import { MultiIdTagItem } from './items/multi_id';
import { NewMoscowTagItem } from './items/new_moscow';
import { NotLastFloorTagItem } from './items/not_last_floor';
import { OfferIdTagItem } from './items/offer_id';
import { OfficeConditionTypesTagItem } from './items/office_condition';
import { ParkingTypesTagItem } from './items/parking';
import { PenthouseTagItem } from './items/penthouse';
import { PhoneTagItem } from './items/phone';
import { PlacementTypeTagItem } from './items/placement_type';
import { PromoTagItem } from './items/promo';
import { PropertyTypeTagItem } from './items/property_type';
import { PublishPeriodTagItem } from './items/publish_period';
import { RentTimeTagItem } from './items/rent_time';
import { RepairTagItem } from './items/repair';
import { RoomsInDealTagItem } from './items/rooms_in_deal';
import { RoomsTotalTagItem } from './items/rooms_total';
import { SaleTypesTagItem } from './items/sale_type';
import { SuburbanUtilityTagItem } from './items/suburban_utility';
import { SuburbanWCTypeTagItem } from './items/suburban_wc';
import { TenantsTagItem } from './items/tenants';
import { TotalFloorsTagItem } from './items/total_floors';
import { UserIdTagItem } from './items/user_id';
import { WCCountTagItem } from './items/wc_count';
import { WCTypeTagItem } from './items/wc_type';
import { WindowsTypeTagItem } from './items/windows';
import { WithPhotoTagItem } from './items/with_photo';
import { WithoutDepositTagItem } from './items/without_deposit';
import { WordsExcludeTagItem } from './items/words_exclude';
import { WordsIncludeTagItem } from './items/words_include';
import { UndergroundTravelContainer } from './underground_travel/container';
import { tagThemeSmall } from './tag_theme';
import { RailwayTagItem } from './items/geo/railway';
import { TGeoSpecialTag, trackGeoTagDeleted, trackClearButtonClick } from './trackings';
import { WithLayoutTagItem } from './items/with_layout';
import { IdenticalOffersIdTagItem } from './items/identical_offers_id';
import { AmenityTagItem } from './items/amenity';
import { CoworkingAccessTag } from './items/coworking_access';
import { NewbuildingHouseTagItem } from './items/newbuildig_house';
import { TagNew } from './TagNew';
import { CoworkingIdTag } from './items/coworking_id';

const style = require('./index.css');

export interface ITagsProps {
  tags: TTag[];
  travelType: EMetroTravelType;
  travelTime: number | undefined;
  jsonQuery: IJsonQuery;
  isFitInOneLineEnabled: boolean;
  userIsAuthenticated: boolean;
  isNewAdvancedFilters: boolean;
  withNewTag: boolean;

  onTagDeleted(tag: TTag, index: number): void;
  onAllTagsDeleted(): void;
}

export interface ITagsOwnProps {
  isAdvancedFiltersOpened?: boolean;
}

export interface ITagsState {
  isUndergroundTravelHidden: boolean;
  tagsMaxWidth: number;
}

const GEO_SPECIAL_TYPES_TAGS: TGeoSpecialTag[] = [
  'geo-location',
  'geo-district',
  'geo-street',
  'geo-underground',
  'geo-railway',
  'geo-newobject',
];

export type TTagsProps = ITagsProps & ITagsOwnProps;

export class Tags extends React.Component<TTagsProps, ITagsState> {
  public state = {
    isUndergroundTravelHidden: false,
    tagsMaxWidth: 0,
  };

  private buttons: HTMLDivElement | null = null;
  private undergroundTravel: HTMLDivElement | null = null;
  private wrapper: HTMLDivElement | null = null;

  public componentDidMount() {
    this.updateTagsMaxWidth();
    window.addEventListener('resize', this.updateTagsMaxWidth);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.updateTagsMaxWidth);
  }

  public componentWillReceiveProps(nextProps: ITagsProps) {
    if (
      (nextProps.travelType !== EMetroTravelType.Any &&
        nextProps.travelTime &&
        (nextProps.travelType !== this.props.travelType || nextProps.travelTime !== this.props.travelTime)) ||
      nextProps.tags.filter(tag => tag.type === 'geo-underground').length !==
        this.props.tags.filter(tag => tag.type === 'geo-underground').length
    ) {
      this.setState({
        isUndergroundTravelHidden: false,
      });
    }
  }

  public componentDidUpdate(prevProps: ITagsProps) {
    const clearButtonTagsLengthReached = prevProps.tags.length < 2 && this.props.tags.length >= 2;

    if (clearButtonTagsLengthReached || prevProps.isFitInOneLineEnabled !== this.props.isFitInOneLineEnabled) {
      this.updateTagsMaxWidth();
    }
  }

  public render() {
    const {
      isFitInOneLineEnabled,
      tags,
      travelType,
      travelTime,
      jsonQuery,
      isNewAdvancedFilters,
      withNewTag,
    } = this.props;
    const { isUndergroundTravelHidden } = this.state;

    const offerType = offerTypeFromJsonQuery(jsonQuery);

    return (
      <div
        ref={wrapper => (this.wrapper = wrapper)}
        {...mergeStyles([
          style['container'],
          isFitInOneLineEnabled && style['container--line'],
          withNewTag && style['container_new-tag'],
        ])}
      >
        {!isUndergroundTravelHidden &&
          (tags.some(tag => tag.type === 'geo-underground') || travelType !== EMetroTravelType.Any || travelTime) && (
            <div
              ref={undergroundTravel => (this.undergroundTravel = undergroundTravel)}
              className={style['specialTags']}
            >
              <UndergroundTravelContainer onDelete={this.handleUndergroundTravelDelete} />
            </div>
          )}
        <FitInOneLineContainer maxWidth={this.state.tagsMaxWidth}>
          {tags.map((tag, index) => {
            return (
              <div className={classNames(style['tag'], withNewTag && style['tag_new'])} key={index}>
                {renderTag(tag, () => this.onTagDeleted(tag, index), offerType, isNewAdvancedFilters, withNewTag)}
              </div>
            );
          })}
        </FitInOneLineContainer>
        {tags.length >= 2 && (
          <div ref={buttons => (this.buttons = buttons)} className={style['buttons']}>
            {withNewTag && (
              <Button size="XS" theme="fill_secondary" onClick={this.handleAllTagsDeleted}>
                Очистить
              </Button>
            )}
            {!withNewTag && (
              <ButtonPrev
                theme="primary"
                mode="light"
                size="small"
                {...mergeStyles(style['clear'])}
                onClick={this.handleAllTagsDeleted}
              >
                Очистить
              </ButtonPrev>
            )}
          </div>
        )}
      </div>
    );
  }

  private handleAllTagsDeleted = () => {
    trackClearButtonClick(this.props.isAdvancedFiltersOpened);
    this.props.onAllTagsDeleted();
  };

  private onTagDeleted = (tag: TTag, index: number) => {
    const tagType = tag.type as TGeoSpecialTag;

    /**
     * Если это гео тэг улицы, района, шоссе, региона или ЖД - отправляем аналитику
     */
    if (GEO_SPECIAL_TYPES_TAGS.includes(tagType)) {
      trackGeoTagDeleted(tagType);
    }

    this.props.onTagDeleted(tag, index);
  };

  private updateTagsMaxWidth = () => {
    if (!this.wrapper) {
      return;
    }

    let tagsMaxWidth = this.wrapper.getBoundingClientRect().width;

    if (this.undergroundTravel) {
      const computedStyle = getComputedStyle(this.undergroundTravel);
      tagsMaxWidth -= this.undergroundTravel.getBoundingClientRect().width;
      tagsMaxWidth -= getValueFromComputedStyle(computedStyle, 'marginRight');
    }

    if (this.buttons) {
      const computedStyle = getComputedStyle(this.buttons);
      tagsMaxWidth -= this.buttons.getBoundingClientRect().width;
      tagsMaxWidth -= getValueFromComputedStyle(computedStyle, 'marginLeft');
    }

    this.setState({
      tagsMaxWidth,
    });
  };

  private handleUndergroundTravelDelete = () => {
    this.setState({
      isUndergroundTravelHidden: true,
    });
  };
}

function renderTag(
  tag: TTag,
  onDeleteClick: () => void,
  offerType: FOfferType,
  isNewAdvancedFilters: boolean,
  withNewTag: boolean,
): React.ReactNode {
  const commercialOfferTypes = [
    FOfferType.Office,
    FOfferType.TradeArea,
    FOfferType.Warehouse,
    FOfferType.FreeAppointmentObject,
    FOfferType.PublicCatering,
    FOfferType.Garage,
    FOfferType.Manufacture,
    FOfferType.AutoService,
    FOfferType.Business,
    FOfferType.Building,
    FOfferType.DomesticServices,
    FOfferType.CommercialLand,
    FOfferType.Coworking,
  ];

  const paramTitleHelper = getParamTitleHelper(commercialOfferTypes, offerType);
  const TagComponent = withNewTag ? TagNew : Tag;

  // tslint:disable-next-line:switch-default
  switch (tag.type) {
    case 'deal_rent':
      return (
        withNewTag && (
          <TagNew onDeleteClick={onDeleteClick} key="deal-rent">
            Сдай/Сними
          </TagNew>
        )
      );
    case 'advanced-contract-type':
      return (
        <ContractTypeTagItem
          key="ContractTypeTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-commercial-specialty':
      return (
        <TagComponent key="advanced-commercial-specialty" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          {tag.text}
        </TagComponent>
      );

    case 'advanced-comission-comissionType':
      return (
        <ComissionTypeTagItem
          key="ComissionTypeTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-comission-agentCallsForbidden':
      return (
        <AgentCallsForbiddenTagItem
          key="AgentCallsForbiddenTagItem"
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-has-equipment':
      return (
        <TagComponent key="advanced-has-equipment" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          {tag.value ? 'С оборудованием' : 'Без оборудования'}
        </TagComponent>
      );

    case 'advanced-has-extinguishing-system':
      return (
        <TagComponent key="advanced-has-extinguishing-system" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          Есть система пожаротушения
        </TagComponent>
      );

    case 'advanced-has-heating':
      return (
        <TagComponent key="advanced-has-heating" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          Есть отопление
        </TagComponent>
      );

    case 'advanced-has-electricity':
      return (
        <TagComponent key="advanced-has-electricity" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          Есть электричество
        </TagComponent>
      );

    case 'advanced-has-light':
      return (
        <TagComponent key="advanced-has-light" onDeleteClick={onDeleteClick}>
          Есть свет
        </TagComponent>
      );

    case 'advanced-has-water':
      return (
        <TagComponent key="advanced-has-water" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          Есть водоснабжение
        </TagComponent>
      );

    case 'advanced-has-video':
      return (
        <TagComponent key="advanced-has-video" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          Только с видео
        </TagComponent>
      );

    case 'advanced-heating':
      return (
        <HeatingTypesTagItem
          key="HeatingTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-balconies-balcony':
      return <BalconyTagItem key="BalconyTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-balconies-loggia':
      return <LoggiaTagItem key="LoggiaTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-tenants':
      return <TenantsTagItem key="TenantsTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-houseYear':
      return (
        <HouseYearTagItem key="HouseYearTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-phone':
      return <PhoneTagItem key="PhoneTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-placement-type':
      return (
        <PlacementTypeTagItem
          key="PlacementTypeTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-decoration':
      return (
        <DecorationTagItem key="DecorationTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-demolished-in-moscow-programm':
      return (
        <TagComponent key="DemolishedInMoscowProgrammTag" onDeleteClick={onDeleteClick}>
          {tag.value ? 'Только дома под снос' : 'Не показывать дома под снос'}
        </TagComponent>
      );

    case 'advanced-developer-type':
      return (
        <DeveloperTagItem key="DeveloperTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-developer-promo':
      return <PromoTagItem key="PromoTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-landStatus':
      return (
        <LandStatusTypesTagItem
          key="LandStatusTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-offerId':
      return <OfferIdTagItem key="OfferTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'userId':
      return <UserIdTagItem key="UserIdTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-parking':
      return (
        <ParkingTypesTagItem key="ParkingTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-suburbanWC':
      return (
        <SuburbanWCTypeTagItem
          key="SuburbanWCTypeTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-doneYear-isDone':
      return (
        <TagComponent key="BuildingDoneTagItem-isDone" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          Дом сдан
        </TagComponent>
      );

    case 'advanced-doneYear-years':
      return (
        <TagComponent key="BuildingDoneTagItem-years" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          Год сдачи {tag.value}
        </TagComponent>
      );

    case 'advanced-doneYear-after':
      return (
        <BuildingDoneTagItem
          key="BuildingDoneTagItem-after"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-bedroomsCount':
      return (
        <BedroomsMinCountTagItem
          key="BedroomsMinCountTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-wc-wcType':
      return <WCTypeTagItem key="WCTypeTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-wc-wcCount':
      return <WCCountTagItem key="WCCountTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-windows':
      return (
        <WindowsTypeTagItem key="WindowsTypeTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-ceilingHeight':
      return (
        <CeilingHeightTagItem
          key="CeilingHeightTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-layout':
      return (
        <LayoutTypeTagItem key="LayoutTypeTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-elevators-minCount':
      return (
        <ElevatorsMinCountTagItem
          key="ElevatorsMinCountTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          isNewAdvancedFilters={isNewAdvancedFilters}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-elevators-hasService':
      return (
        <ElevatorsHasServiceTagItem
          key="ElevatorsHasServiceTagItem"
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-rentTime':
      return <RentTimeTagItem key="RentTimeTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-floor-floor':
      return <FloorTagItem key="FloorTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-floor-isFirstFloor':
      return (
        <IsFirstFloorTagItem
          key="IsFirstFloorTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-floor-isBasementFloor':
      return (
        <IsBasementFloorTagItem key="IsBasementFloorTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-floor-isSemibasementFloor':
      return (
        <IsSemibasementFloorTagItem
          key="IsSemibasementFloorTagItem"
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-floor-notLastFloor':
      return (
        <NotLastFloorTagItem
          key="NotLastFloorTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-roomsInDeal':
      return (
        <RoomsInDealTagItem key="RoomsInDealTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-roomsTotal':
      return (
        <RoomsTotalTagItem key="RoomsTotalTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-facilities-facility':
      return <FacilityTagItem key="FacilityTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-suburbanUtility':
      return (
        <SuburbanUtilityTagItem
          key="SuburbanUtilityTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-facilities-furniture':
      return (
        <FurnitureTagItem key="FurnitureTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-dwellingStatus-apartment':
      return (
        <ApartmentTagItem key="ApartmentTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-dwellingStatus-penthouse':
      return <PenthouseTagItem key="PenthouseTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-rentTerms-maxPrepay':
      return (
        <MaxPrepayTagItem key="MaxPrepayTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-rentTerms-withoutDeposit':
      return (
        <WithoutDepositTagItem key="WithoutDepositTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-wordsInclude':
      return (
        <WordsIncludeTagItem
          key="WordsIncludeTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-wordsExclude':
      return (
        <WordsExcludeTagItem
          key="WordsExcludeTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-saleType-mortgagePossible':
      return (
        <MortgagePossibleTagItem key="MortgagePossibleTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-saleType-saleType':
      return (
        <SaleTypesTagItem key="SaleTypesTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-buildingClass':
      return (
        <BuildingClassTypesTagItem
          key="BuildingClassTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          paramsDescription={paramTitleHelper([FOfferType.Office, FOfferType.TradeArea, FOfferType.Building])}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-warehouseBuildingClass':
      return (
        <WarehouseBuildingClassTypesTagItem
          key="WarehouseBuildingClassTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          paramsDescription={paramTitleHelper([FOfferType.Manufacture, FOfferType.Warehouse])}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-garage':
      return (
        <GarageTypesTagItem key="GarageTypesTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-buildingElevator':
      return (
        <BuildingElevatorTypesTagItem
          key="BuildingElevatorTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-garageKind':
      return (
        <GarageKindsTagItem key="GarageKindsTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-buildingCrane':
      return (
        <BuildingCraneTypesTagItem
          key="BuildingCraneTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-garageMaterial':
      return (
        <GarageMaterialsTagItem
          key="GarageMaterialsTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-buildingGates':
      return (
        <BuildingGatesTypesTagItem
          key="BuildingGatesTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-garageStatus':
      return (
        <GarageStatusesTagItem
          key="GarageStatusesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-buildingHeating':
      return (
        <BuildingHeatingTypesTagItem
          key="BuildingHeatingTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-infrastructure':
      return (
        <InfrastructureParamTagItem
          key="InfrastructureParamTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-estate-type':
      return (
        <EstateTypeTagItem key="EstateTypeTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'advanced-entrance':
      return (
        <EntranceTypesTagItem
          key="EntranceTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          paramsDescription={paramTitleHelper([
            FOfferType.TradeArea,
            FOfferType.FreeAppointmentObject,
            FOfferType.PublicCatering,
            FOfferType.AutoService,
            FOfferType.DomesticServices,
            FOfferType.Building,
            FOfferType.Business,
          ])}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-accessSystem':
      return (
        <AccessSystemTagItem
          key="AccessSystemTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          paramsDescription={paramTitleHelper([FOfferType.Office])}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-officeCondition':
      return (
        <OfficeConditionTypesTagItem
          key="OfficeConditionTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          paramsDescription={paramTitleHelper([FOfferType.Office])}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-manufactureCondition':
      return (
        <ManufactureConditionTypesTagItem
          key="ManufactureConditionTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          paramsDescription={paramTitleHelper([FOfferType.Manufacture, FOfferType.Warehouse])}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-by-homeowner':
      return (
        <TagComponent key="advanced-by-homeowner" tagStyles={tagThemeSmall} onDeleteClick={onDeleteClick}>
          От собственника
        </TagComponent>
      );

    case 'advanced-buildingCondition':
      return (
        <BuildingConditionTypesTagItem
          key="BuildingConditionTypesTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          paramsDescription={paramTitleHelper([
            FOfferType.TradeArea,
            FOfferType.FreeAppointmentObject,
            FOfferType.PublicCatering,
            FOfferType.AutoService,
            FOfferType.DomesticServices,
            FOfferType.Building,
            FOfferType.Business,
          ])}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-withPhoto':
      return <WithPhotoTagItem key="WithPhotoTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-withLayout':
      return <WithLayoutTagItem key="WithLayoutTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'totalArea':
      return <AreaTagItem key="TotalAreaTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'roomArea':
      return (
        <AreaTagItem
          key="RoomAreaTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          title="Площадь комнаты"
          withNewTag={withNewTag}
        />
      );

    case 'kitchenArea':
      return (
        <AreaTagItem
          key="KitchenAreaTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          title="Площадь кухни"
          withNewTag={withNewTag}
        />
      );

    case 'siteArea':
      return (
        <AreaTagItem
          key="KitchenAreaTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          title={'Площадь участка'}
          unit={'сот.'}
          withNewTag={withNewTag}
        />
      );

    case 'livingArea':
      return (
        <AreaTagItem
          key="LivingAreaTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          title="Жилая площадь"
          withNewTag={withNewTag}
        />
      );

    case 'totalFloors':
      return (
        <TotalFloorsTagItem key="TotalFloorsTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'publishPeriod':
      return (
        <PublishPeriodTagItem
          key="PublishPeriodTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-house-line-building':
      return (
        <HouseLineBuildingTag
          key="HouseLineBuildingTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-house-line-shopping-area':
      return (
        <HouseLineShoppingAreaTag
          key="HouseLineShoppingAreaTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'houseMaterial':
      return (
        <HouseMaterialTagItem
          key={`HouseMaterialTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'repair':
      return (
        <RepairTagItem
          key={`RepairTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'bs_center':
      return (
        <BSCenterTagItem
          key={`BSCenterTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-location':
      return (
        <GeoLocationTagItem
          key={`GeoLocationTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-newobject':
      return (
        <GeoNewobjectTagItem
          key={`GeoNewobjectTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'newbuilding-house':
      return (
        <NewbuildingHouseTagItem
          key={`NewbuildingHouse-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-street':
      return (
        <GeoStreetTagItem
          key={`GeoStreetTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-house':
      return (
        <GeoHouseTagItem
          key={`GeoHouseTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-underground':
      return (
        <GeoUndergroundTagItem
          key={`GeoUndergroundTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-railway':
      return (
        <RailwayTagItem
          key={`GeoRailwayTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-district':
      return (
        <GeoDistrictTagItem
          key={`GeoDistrictTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-highway':
      return (
        <GeoHighwayTagItem
          key={`GeoHighwayTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-builder':
      return (
        <GeoBuilderTagItem
          key={`GeoBuilderTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-polygon':
      return (
        <GeoPolygonTagItem
          key={`GeoPolygonTagItem-${tag.value}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'geo-bbox':
      return <GeoBboxTagItem key="GeoBboxTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'geo-circle':
      return (
        <GeoCircleTagItem
          key={`GeoCircleTagItem-${tag.center}-${tag.radius}`}
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'IncludeNewMoscow':
      return (
        <NewMoscowTagItem key="NewMoscowTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'is-in-hidden-base':
      return (
        <HiddenBaseTagItem key="HiddenBaseTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    case 'multiId':
      return <MultiIdTagItem key="MultiIdTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'advanced-buildingType':
      return (
        <BuildingTypeTagItem
          key="BuildingTypeTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-buildingTypeBuilding':
      return (
        <BuildingTypeBuildingTagItem
          key="BuildingTypeBuildingTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-propertyType':
      return (
        <PropertyTypeTagItem
          key="PropertyTypeTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'kp-id':
      return <KpIdTagItem key="KpIdTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'domrf':
      return <DomrfTagItem key="DomrfTagItem" onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'identicalOffersId':
      return (
        <IdenticalOffersIdTagItem
          key="IdenticalOffersIdTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'advanced-amenity':
      return <AmenityTagItem key="AmenityTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />;

    case 'coworking-access':
      return (
        <CoworkingAccessTag
          key="CoworkingAccessTagItem"
          tag={tag}
          onDeleteClick={onDeleteClick}
          withNewTag={withNewTag}
        />
      );

    case 'coworking_id':
      return (
        <CoworkingIdTag key="CoworkingIdTagItem" tag={tag} onDeleteClick={onDeleteClick} withNewTag={withNewTag} />
      );

    default:
      return null;
  }
}
